import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { onAuthRequired } from '@/utils/auth';
import { authenticatedFetch } from '../utils/api';
import { API_URLS } from '../constants';
import { useModels } from '../hooks/useModels';
import { useAuth } from '../contexts/AuthContext';
import { AddPromptToCollectionModal } from './collections';
import CommentsSection from './CommentsSection';
import SaveNewVersionModal from './SaveNewVersionModal';
import VersionHistoryDisplay from './VersionHistoryDisplay';
import { PromptTabNavigation } from './ui/prompt';
import { enhancePrompt } from '@/utils/promptAgent';

interface Prompt {
  id: string;
  category_id: number;
  created_at: string;
  description: string;
  full_text: string;
  is_public: boolean;
  model_type: string;
  modified_at: string;
  posted_at: string;
  title: string;
  updated_at: string;
  user_id: string;
  views: number;
  votes: number;
  systemprompt?: string;
  total_views: number;
  total_copies: number;
  prompt_type: 'user' | 'admin';
}

interface PromptVersion {
  id: string;
  version_number: number;
  title: string;
  description: string;
  full_text: string;
  change_notes: string;
  created_at: string;
  created_by_name: string;
  systemprompt?: string;
}

const PromptDetailComponent: React.FC = () => {
  const { id } = useParams<{ id: string; }>();
  const navigate = useNavigate();
  const { models, loading: modelsLoading } = useModels();
  const { isLoggedIn, user } = useAuth();
  const [prompt, setPrompt] = useState<Prompt | null>(null);
  const [latestVersion, setLatestVersion] = useState<PromptVersion | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'current' | 'playground' | 'versions'>('current');
  const [showShare, setShowShare] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showSaveButtonInOverview] = useState(false);
  const [showAddToCollectionModal, setShowAddToCollectionModal] = useState(false);
  const [isPublic, setIsPublic] = useState(prompt?.is_public);
  const [totalCopies, setTotalCopies] = useState<number>(0);
  const [promptInCollections, setPromptInCollections] = useState<Set<number>>(new Set());
  const [isCheckingCollections, setIsCheckingCollections] = useState(false);
  const [latestSystemPrompt, setLatestSystemPrompt] = useState<string>('');
  const [playgroundData, setPlaygroundData] = useState({
    selectedModel: '',
    systemMessage: '',
    promptText: '',
    changeNotes: ''
  });
  
  // Enhancement state for playground
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [enhancementError, setEnhancementError] = useState<string | null>(null);
  const [isEnhanced, setIsEnhanced] = useState(false);
  const [hasBeenEnhanced, setHasBeenEnhanced] = useState(false);
  const [originalPromptText, setOriginalPromptText] = useState<string>('');
  const [enhancedPromptText, setEnhancedPromptText] = useState<string>('');
  // Get the content to display (latest version if available, otherwise original prompt)
  const displayContent = {
    title: latestVersion?.title || prompt?.title || '',
    description: latestVersion?.description || prompt?.description || '',
    full_text: latestVersion?.full_text || prompt?.full_text || '',
    systemprompt: latestSystemPrompt || latestVersion?.systemprompt || prompt?.systemprompt || ''
  };
  
  const isOwner = user?.id === prompt?.user_id;

  // Enhancement functions for playground
  const handleEnhancePrompt = useCallback(async () => {
    if (!playgroundData.promptText.trim()) {
      setEnhancementError('Please enter a prompt to enhance');
      toast.error('Please enter a prompt to enhance');
      return;
    }

    // If this text has already been enhanced, return the cached enhanced version
    if (hasBeenEnhanced && originalPromptText === playgroundData.promptText.trim() && enhancedPromptText) {
      setIsEnhanced(true);
      setPlaygroundData(prev => ({ ...prev, promptText: enhancedPromptText }));
      return;
    }

    setIsEnhancing(true);
    setEnhancementError(null);

    try {
      // Store original prompt before enhancement
      setOriginalPromptText(playgroundData.promptText.trim());
      const enhanced = await enhancePrompt(playgroundData.promptText);
      setEnhancedPromptText(enhanced);
      setIsEnhanced(true);
      setHasBeenEnhanced(true);
      setPlaygroundData(prev => ({ ...prev, promptText: enhanced }));
      toast.success('Prompt enhanced successfully!');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to enhance prompt';
      setEnhancementError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsEnhancing(false);
    }
  }, [playgroundData.promptText, hasBeenEnhanced, originalPromptText, enhancedPromptText]);

  const handleRevertToOriginal = useCallback(() => {
    setIsEnhanced(false);
    setEnhancementError(null);
    setPlaygroundData(prev => ({ ...prev, promptText: originalPromptText }));
    // Keep hasBeenEnhanced and cached texts for potential re-toggle
  }, [originalPromptText]);

  const handleToggleToEnhanced = useCallback(() => {
    if (hasBeenEnhanced && enhancedPromptText) {
      setIsEnhanced(true);
      setPlaygroundData(prev => ({ ...prev, promptText: enhancedPromptText }));
    }
  }, [hasBeenEnhanced, enhancedPromptText]);

  const fetchPromptDetails = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      
      // Fetch base prompt details
      const response = await fetch(API_URLS.PROMPTS.DETAIL(id), {
        credentials: 'include'
      });

      if (!response.ok) {
        if (response.status === 401) {
          setError('Authentication required. Please log in to view this prompt.');
          // Redirect to auth page after a short delay
          setTimeout(() => {
            window.location.href = '/auth';
          }, 2000);
        } else if (response.status === 403) {
          setError('Access denied. You do not have permission to view this prompt.');
        } else if (response.status === 404) {
          setError('Prompt not found');
        } else {
          throw new Error('Failed to fetch prompt details');
        }
        return;
      }

      const data = await response.json();
      setPrompt(data);

      // Fetch latest version
      try {
        const versionsResponse = await authenticatedFetch(API_URLS.PROMPTS.VERSIONS.LIST(id));
        if (versionsResponse.ok) {
          const versionsData = await versionsResponse.json();
          if (versionsData.length > 0) {
            // Versions are ordered by version_number DESC, so first one is latest
            const latest = versionsData[0];
            setLatestVersion(latest);
            setLatestSystemPrompt(latest.systemprompt || '');
            
            // Use latest version content for playground
            setPlaygroundData(prev => ({
              ...prev,
              promptText: latest.full_text || data.full_text || ''
            }));
          } else {
            // No versions exist, use original prompt
            setPlaygroundData(prev => ({
              ...prev,
              promptText: data.full_text || ''
            }));
          }
        } else {
          // If versions API fails, fall back to original prompt
          setPlaygroundData(prev => ({
            ...prev,
            promptText: data.full_text || ''
          }));
        }
      } catch (versionError) {
        console.warn('Failed to fetch latest version, using original:', versionError);
        setPlaygroundData(prev => ({
          ...prev,
          promptText: data.full_text || ''
        }));
      }
    } catch (err) {
      console.error('Error fetching prompt details:', err instanceof Error ? err.message : 'Unknown error');
      setError('Failed to load prompt details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Check which collections contain this prompt
  const checkPromptCollections = useCallback(async () => {
    if (!id || !isLoggedIn) return;

    try {
      setIsCheckingCollections(true);
      
      // Fetch user's collections
      const collectionsResponse = await authenticatedFetch('/api/collections');
      if (!collectionsResponse.ok) {
        throw new Error('Failed to fetch collections');
      }
      
      const collectionsData = await collectionsResponse.json();
      const userCollections = collectionsData.data || [];
      
      // Check which collections contain this prompt
      const collectionsWithPrompt = new Set<number>();
      for (const collection of userCollections) {
        try {
          const checkResponse = await authenticatedFetch(`/api/collections/${collection.id}/prompts`);
          if (checkResponse.ok) {
            const promptsData = await checkResponse.json();
            const prompts = promptsData.data || [];
            const hasPrompt = prompts.some((promptItem: { id: number }) => promptItem.id === parseInt(id));
            if (hasPrompt) {
              collectionsWithPrompt.add(collection.id);
            }
          }
        } catch (error) {
          console.error(`Error checking collection ${collection.id}:`, error);
        }
      }
      
      setPromptInCollections(collectionsWithPrompt);
    } catch (error) {
      console.error('Error checking prompt collections:', error);
    } finally {
      setIsCheckingCollections(false);
    }
  }, [id, isLoggedIn]);

  useEffect(() => {
    fetchPromptDetails();
  }, [fetchPromptDetails]);

  useEffect(() => {
    checkPromptCollections();
  }, [checkPromptCollections]);

  useEffect(() => {
    if (models.length > 0 && !playgroundData.selectedModel) {
      // Use the prompt's model_type if available, otherwise default to first model
      const defaultModel = prompt?.model_type && models.find(m => m.name === prompt.model_type)
        ? prompt.model_type
        : models[0].name;

      setPlaygroundData(prev => ({
        ...prev,
        selectedModel: defaultModel
      }));
    }
  }, [models, playgroundData.selectedModel, prompt?.model_type]);

  useEffect(() => {
    if (prompt) {
      setIsPublic(prompt.is_public);
    }
  }, [prompt]);

  // Reset enhancement state when switching away from playground
  useEffect(() => {
    if (activeTab !== 'playground') {
      setIsEnhanced(false);
      setHasBeenEnhanced(false);
      setIsEnhancing(false);
      setEnhancementError(null);
      setOriginalPromptText('');
      setEnhancedPromptText('');
    }
  }, [activeTab]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleShare = () => {
    setShowShare(!showShare);
  };

  const saveCopyEvent = async () => {
    try {
      await fetch(API_URLS.ANALYTICS.PROMPT_EVENT, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          promptId: id,
          userId: user?.id,
          eventType: 'copy',
        })
      });

      fetchPromptCopies();
    } catch (err) {
      console.error('Failed to register copy event:', err instanceof Error ? err.message : 'Unknown error');
      toast.error('Failed to register copy');
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/prompts/${prompt?.id}`);
      toast.success('Link copied to clipboard!');
      setShowShare(false);
    } catch (err) {
      console.error('Failed to copy:', err instanceof Error ? err.message : 'Unknown error');
      toast.error('Failed to copy link');
    }
  };

  const handleCopyText = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      saveCopyEvent();
      toast.success(`${type} copied to clipboard!`);
    } catch (err) {
      console.error('Failed to copy:', err instanceof Error ? err.message : 'Unknown error');
      toast.error(`Failed to copy ${type.toLowerCase()}`);
    }
  };

  const fetchPromptCopies = useCallback(async () => {
    try {
      if (prompt) {
        const response = await fetch(API_URLS.ANALYTICS.PROMPT_EVENT_DETAIL(Number(prompt.id)));
        if (response.ok) {
          const data = await response.json();
          setTotalCopies(data.totalCopies);
        }
      }
    } catch (err) {
      console.error('Error fetching prompt copies:', err instanceof Error ? err.message : 'Unknown error');
      setError('Failed to load prompt copies');
    }
  }, [prompt]);

  useEffect(() => {
    fetchPromptCopies();
  }, [fetchPromptCopies]);

  const togglePromptVisibility = async () => {
    try {
      if (prompt) {
        const response = await authenticatedFetch(API_URLS.PROMPTS.TOGGLE_VISIBILITY(Number(prompt.id)), {
          method: 'PATCH'
        });

        if (response.ok) {
          const updatedPrompt = await response.json();
          const promptVisibility = updatedPrompt.prompt.isPublic;
          setPrompt(prev => ({ ...prev!, is_public: promptVisibility }));
          setIsPublic(promptVisibility);
          toast.success(`Prompt is now ${promptVisibility ? 'public' : 'private'}`);
        } else {
          toast.error('Failed to toggle prompt visibility');
        }
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to update prompt visibility');
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();
    return isToday ? 'Today' : date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-3/4 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-6"></div>
            <div className="space-y-3">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
              <div className="h-4 bg-gray-200 rounded w-4/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="text-red-600 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{error}</h2>
          <p className="text-gray-600 mb-4">The prompt you're looking for might have been removed or doesn't exist.</p>
          <button
            onClick={handleBack}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-brand-dark-blue hover:bg-brand-electric-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-electric-blue"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!prompt) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Prompt not found</h2>
          <p className="text-gray-600 mb-4">The prompt you're looking for doesn't exist.</p>
          <button
            onClick={handleBack}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-brand-dark-blue hover:bg-brand-electric-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-electric-blue"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl" data-testid="prompt-detail">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-brand-dark-blue">
              {displayContent.title} 
              <span className="text-sm font-normal text-gray-500">(User Generated)</span>
              {latestVersion && (
                <span className="text-sm font-normal text-blue-600 ml-2">
                  - Latest Version {latestVersion.version_number}
                </span>
              )}
            </h1>
            <div className="relative">
              <button
                onClick={handleShare}
                aria-label="Share prompt"
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-brand-light-gray focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-electric-blue"
              >
                <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                </svg>
                Share
              </button>

              {showShare && (
                <div className="absolute right-0 mt-2 w-72 bg-white rounded-md shadow-lg z-10 p-4">
                  <div className="flex flex-col space-y-3">
                    <input
                      type="text"
                      readOnly
                      aria-label="Share URL"
                      value={`${window.location.origin}/prompts/${prompt.id}`}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                    <button
                      onClick={handleCopy}
                      aria-label="Copy link"
                      className="w-full px-4 py-2 text-sm font-medium text-white bg-brand-dark-blue rounded-md hover:bg-brand-electric-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-electric-blue"
                    >
                      Copy link
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <PromptTabNavigation
          isLoggedIn={isLoggedIn}
          activeTab={activeTab}
          prompt={prompt}
          setActiveTab={setActiveTab}
          setShowAddToCollectionModal={setShowAddToCollectionModal}
          promptInCollections={promptInCollections}
          isCheckingCollections={isCheckingCollections}
        />

        {/* Tab Content */}
        <div className="flex flex-col lg:flex-row">
          {activeTab === 'current' && (
            <>
              {/* Left Side Content */}
              <div className="w-full lg:flex-1 p-6 space-y-6">

                {/* Prompt */}
                <>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-gray-700">
                      Prompt {latestVersion && (
                        <span className="text-xs text-blue-600 font-normal">
                          (v{latestVersion.version_number} - Latest)
                        </span>
                      )}
                    </h3>
                    <button
                      onClick={() => handleCopyText(displayContent.full_text, 'Prompt')}
                      className="flex items-center text-gray-500 hover:text-gray-700 text-xs"
                      aria-label="Copy prompt"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      <span className="ml-1">Copy</span>
                    </button>
                  </div>
                  <div className="bg-white border border-gray-300 rounded-lg p-4">
                    <div className="text-sm text-start text-gray-800 whitespace-pre-wrap font-mono">
                      {displayContent.full_text}
                    </div>
                  </div>
                </>

                {/* System Message */}
                {displayContent.systemprompt && (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-medium text-gray-700">System Prompt</h3>
                      <button
                        onClick={() => handleCopyText(displayContent.systemprompt!, 'System Message')}
                        className="flex items-center text-gray-500 hover:text-gray-700 text-xs"
                        aria-label="Copy system message"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        <span className="ml-1">Copy</span>
                      </button>
                    </div>
                    <div className="bg-white border border-gray-300 rounded-lg p-4">
                      <div className="text-sm text-start text-gray-800">{displayContent.systemprompt}</div>
                    </div>
                  </>
                )}

                {/* Version Info */}
                {latestVersion && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium text-blue-900">Latest Version Info</h4>
                      <span className="text-xs text-blue-600">Version {latestVersion.version_number}</span>
                    </div>
                    {latestVersion.change_notes && (
                      <p className="text-sm text-blue-800 mb-2">
                        <strong>Changes:</strong> {latestVersion.change_notes}
                      </p>
                    )}
                    <p className="text-xs text-blue-600">
                      Updated {new Date(latestVersion.created_at).toLocaleDateString()} by {latestVersion.created_by_name}
                    </p>
                  </div>
                )}                {/* Comments Section */}
                <CommentsSection
                  promptId={Number(id)}
                  isLoggedIn={isLoggedIn}
                  onAuthRequired={onAuthRequired}
                  currentUserId={user!.id}
                />
              </div>

              {/* Right Side Content */}
              <div className="w-full lg:w-80 p-6 bg-gray-50 border-l border-gray-200 space-y-6">
                {/* Model Selection */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-bold text-gray-900">Model</h3>
                    <button
                      onClick={() => handleCopyText('OpenAI/gpt-4o-mini', 'Model')}
                      className="flex items-center text-gray-500 hover:text-gray-700 text-xs"
                      aria-label="Copy model"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      <span className="ml-1">Copy</span>
                    </button>
                  </div>
                  <div className="text-sm font-medium text-gray-900 mb-1">OpenAI/gpt-4o-mini</div>
                  <div className="text-xs text-gray-500 mb-2">8,192 4,096 tokens</div>
                  <div className="text-xs text-gray-600">$0.03 / $0.06</div>
                </div>

                {/* About Section */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h3 className="text-sm font-bold text-gray-900 mb-3">About</h3>

                  {/* Statistics */}
                  <div className="space-y-2 mb-3">
                    <div className="flex items-center text-sm">
                      <svg className="h-4 w-4 mr-2 text-brand-electric-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      <span className="text-gray-600">Views:&nbsp;</span>
                      <span className="text-green-600 font-medium">
                        {prompt.views.toLocaleString()}
                      </span>
                    </div>

                    <div className="flex items-center text-sm">
                      <svg className="h-4 w-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      <span className="text-gray-600">Copies:&nbsp;</span>
                      <span className="text-green-600 font-medium">{totalCopies.toLocaleString()}</span>
                    </div>
                  </div>

                  <div className="text-xs text-gray-500">
                    Last updated: {formatDate(prompt.modified_at)}
                  </div>

                  {/* Visibility Toggle */}
                  {isOwner && (
                    <div className="flex items-center justify-center gap-3 my-4">
                      <label htmlFor="public-toggle" className="text-gray-700 font-medium">
                        Visibility:
                      </label>
                      <button
                        id="public-toggle"
                        onClick={togglePromptVisibility}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 ${isPublic ? 'bg-brand-electric-blue' : 'bg-gray-300'}`}
                        role="switch"
                        aria-checked={isPublic}
                        aria-label={`Toggle prompt visibility to ${isPublic ? 'private' : 'public'}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isPublic ? 'translate-x-6' : 'translate-x-1'}`}
                        />
                      </button>
                      <span className="text-sm text-gray-600">{isPublic ? 'Public' : 'Private'}</span>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  {showSaveButtonInOverview && (
                    <button
                      onClick={async () => {
                        if (!playgroundData.promptText.trim()) {
                          toast.error('Prompt text cannot be empty');
                          return;
                        }

                        setIsSaving(true);
                        try {
                          const response = await authenticatedFetch(API_URLS.PROMPTS.VERSIONS.CREATE(id!), {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({
                              title: prompt?.title || 'Updated Prompt',
                              description: prompt?.description || '',
                              full_text: playgroundData.promptText,
                              change_notes: playgroundData.changeNotes || 'Updated via playground',
                              systemprompt: playgroundData.systemMessage,
                              prompt_type: 'user'
                            })
                          });

                          if (!response.ok) {
                            const errorData = await response.json();
                            throw new Error(errorData.message || 'Failed to save new version');
                          }

                          const responseData = await response.json();
                          
                          // Update the latest version with the new version data
                          const newVersionData = {
                            id: responseData.version.id,
                            version_number: responseData.version.version_number,
                            title: responseData.version.title,
                            description: responseData.version.description || '',
                            full_text: responseData.version.full_text,
                            change_notes: responseData.version.change_notes || '',
                            created_at: responseData.version.created_at,
                            created_by_name: user?.name || 'You'
                          };
                          
                          setLatestVersion(newVersionData);
                          
                          toast.success(`New version ${responseData.version.version_number} saved successfully!`);
                          // Re-fetch prompt details to get the latest version
                          await fetchPromptDetails();
                          setActiveTab('current');
                        } catch (error) {
                          console.error('Error saving new version:', error instanceof Error ? error.message : 'Unknown error');
                          toast.error(error instanceof Error ? error.message : 'Failed to save new version');
                        } finally {
                          setIsSaving(false);
                        }
                      }}
                      disabled={isSaving || !playgroundData.promptText.trim()}
                      className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-brand-dark-blue border border-transparent rounded-md hover:bg-brand-electric-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-electric-blue disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSaving ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Saving...
                        </>
                      ) : (
                        'Save as new version'
                      )}
                    </button>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Main Content Area for other tabs */}
          {activeTab !== 'current' && (
            <div className="flex-1 p-6">

              {activeTab === 'playground' && (
                <div className="flex h-full">
                  {/* Left Side Content */}
                  <div className="flex-1 p-6 space-y-6">

                    {/* System Message */}
                    <div>
                      <label htmlFor="system-message" className="block text-sm font-medium text-gray-700 mb-2">
                        System Prompt (Optional)
                      </label>
                      <textarea
                        id="system-message"
                        value={playgroundData.systemMessage}
                        onChange={(e) => setPlaygroundData(prev => ({ ...prev, systemMessage: e.target.value }))}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-electric-blue focus:border-brand-electric-blue"
                        placeholder="You are a helpful AI assistant..."
                      />
                      <p className="mt-1 text-sm text-gray-500">
                        {playgroundData.systemMessage.length} characters
                      </p>
                    </div>

                    {/* Prompt */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label htmlFor="prompt-text" className="block text-sm font-medium text-gray-700">
                          Prompt
                        </label>
                        <div className="flex items-center space-x-2">
                          {isEnhanced && (
                            <button
                              type="button"
                              onClick={handleRevertToOriginal}
                              className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-700 bg-green-100 border border-green-300 rounded-md hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
                            >
                              <svg className="mr-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
                              </svg>
                              Show Original
                            </button>
                          )}
                          {hasBeenEnhanced && !isEnhanced && (
                            <button
                              type="button"
                              onClick={handleToggleToEnhanced}
                              className="inline-flex items-center px-2 py-1 text-xs font-medium text-brand-dark-blue bg-brand-light-gray border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-electric-blue transition-colors"
                            >
                              <svg className="mr-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                              </svg>
                              Show Enhanced
                            </button>
                          )}
                        </div>
                      </div>
                      <textarea
                        id="prompt-text"
                        value={playgroundData.promptText}
                        onChange={(e) => {
                          setPlaygroundData(prev => ({ ...prev, promptText: e.target.value }));
                          // Reset enhancement state when user manually edits
                          if (isEnhanced || hasBeenEnhanced) {
                            setIsEnhanced(false);
                            setHasBeenEnhanced(false);
                            setOriginalPromptText('');
                            setEnhancedPromptText('');
                          }
                        }}
                        rows={8}
                        className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-brand-electric-blue focus:border-brand-electric-blue font-mono text-sm ${
                          isEnhanced ? 'border-green-300 bg-green-50' : 'border-gray-300'
                        }`}
                        placeholder="Enter your prompt here..."
                      />
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-sm text-gray-500">
                          {playgroundData.promptText.length} characters
                        </p>
                        {isEnhanced && (
                          <span className="text-xs text-green-600 font-medium">Enhanced Version</span>
                        )}
                        {enhancementError && (
                          <span className="text-xs text-red-600">{enhancementError}</span>
                        )}
                      </div>
                    </div>

                    {/* Change Notes */}
                    <div>
                      <label htmlFor="change-notes" className="block text-sm font-medium text-gray-700 mb-2">
                        Change Notes (Optional)
                      </label>
                      <textarea
                        id="change-notes"
                        value={playgroundData.changeNotes}
                        onChange={(e) => setPlaygroundData(prev => ({ ...prev, changeNotes: e.target.value }))}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-electric-blue focus:border-brand-electric-blue"
                        placeholder="Describe what changes you made..."
                      />
                    </div>
                  </div>

                  {/* Right Side Content */}
                  <div className="w-80 p-6 bg-gray-50 border-l border-gray-200 space-y-6">
                    {/* Model Selection Card */}
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <h3 className="text-sm font-bold text-gray-900 mb-3">Model</h3>
                      <select
                        value={playgroundData.selectedModel}
                        onChange={(e) => setPlaygroundData(prev => ({ ...prev, selectedModel: e.target.value }))}
                        className="w-full mb-3 px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-electric-blue focus:border-brand-electric-blue"
                        disabled={modelsLoading}
                      >
                        {modelsLoading ? (
                          <option>Loading models...</option>
                        ) : (
                          models.map((model) => (
                            <option key={model.id} value={model.name}>
                              {model.display_name}
                            </option>
                          ))
                        )}
                      </select>
                      <div className="text-xs text-gray-500 mb-1">8,192 4,096 tokens</div>
                      <div className="text-xs text-gray-600">$0.03 / $0.06</div>
                    </div>

                    {/* Action Buttons */}
                    <div className="space-y-3">
                      <button
                        onClick={() => {
                          const defaultModel = prompt?.model_type && models.find(m => m.name === prompt.model_type)
                            ? prompt.model_type
                            : (models.length > 0 ? models[0].name : '');

                          setPlaygroundData({
                            selectedModel: defaultModel,
                            systemMessage: prompt?.systemprompt || '',
                            promptText: prompt?.full_text || '',
                            changeNotes: ''
                          });
                        }}
                        className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-brand-light-gray focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-electric-blue"
                      >
                        <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Reset
                      </button>
                      <button 
                        onClick={handleEnhancePrompt}
                        disabled={isEnhancing || !playgroundData.promptText.trim()}
                        className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-brand-light-gray focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-electric-blue disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isEnhancing ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Enhancing...
                          </>
                        ) : (
                          <>
                            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            Enhance
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => {
                          if (!playgroundData.promptText.trim()) {
                            toast.error('Prompt text cannot be empty');
                            return;
                          }
                          setShowSaveModal(true);
                        }}
                        disabled={!playgroundData.promptText.trim()}
                        className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-brand-dark-blue border border-transparent rounded-md hover:bg-brand-electric-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-electric-blue disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Save as new version
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <VersionHistoryDisplay
                promptId={id!}
                isVisible={activeTab === 'versions'}
              />
            </div>
          )}
        </div>
      </div>

      {/* Floating Chat Button */}
      <div className="fixed bottom-6 right-6">
        <button
          className="bg-brand-dark-blue hover:bg-brand-electric-blue text-white rounded-full p-3 shadow-lg transition-colors duration-200"
          aria-label="Chat support"
        >
          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        </button>
      </div>

      {/* Save New Version Modal */}
      <SaveNewVersionModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        promptId={id!}
        promptTitle={prompt?.title || ''}
        promptDescription={prompt?.description || ''}
        playgroundSystemMessage={playgroundData.systemMessage}
        playgroundPromptText={playgroundData.promptText}
        playgroundChangeNotes={playgroundData.changeNotes}
        onSuccess={async () => {
          setShowSaveModal(false);
          // Reset enhancement state after successful save
          setIsEnhanced(false);
          setHasBeenEnhanced(false);
          setIsEnhancing(false);
          setEnhancementError(null);
          setOriginalPromptText('');
          setEnhancedPromptText('');
          // Re-fetch prompt details to get the latest version
          await fetchPromptDetails();
          setLatestSystemPrompt(playgroundData.systemMessage || '');
          setActiveTab('current');
        }}
      />

      {/* Add Prompt to Collection Modal */}
      {showAddToCollectionModal && (
        <AddPromptToCollectionModal
          promptId={id!}
          onClose={() => setShowAddToCollectionModal(false)}
          onSuccess={() => {
            setShowAddToCollectionModal(false);
            toast.success('Prompt Collections updated successfully!');
          }}
        />
      )}
    </div>
  );
};

export const PromptDetail = React.memo(PromptDetailComponent);
export default PromptDetail;
